import React, { useCallback } from "react";
import { LogIn, Mail, Lock, Eye, EyeOff } from "lucide-react";
import { GoogleSignInButton } from "../GoogleSignInButton";
import { LoginHighlights } from "../LoginHighlights";

const baseFieldWrapperClasses =
  "flex items-center gap-3 rounded-2xl border border-slate-200 bg-white/85 px-4 py-3 text-sm text-slate-900 shadow-inner shadow-white/40 transition focus-within:border-slate-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-slate-400";

const inputClasses =
  "flex-1 border-0 bg-transparent p-0 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-0";

export const LoginFormCard = ({
  emailInput,
  onEmailChange,
  passwordInput,
  onPasswordChange,
  showPassword,
  onTogglePassword,
  onSubmit,
  loading,
  authError,
  onForgotPassword,
  onRegister,
  onGoogleSignIn,
  onGoogleError,
  googleButtonClasses,
  googleIconWrapperClasses,
  primaryButtonClasses,
}) => {
  const handleGoogleCredential = useCallback(
    async (credential) => {
      if (typeof onGoogleSignIn === "function") {
        return onGoogleSignIn(credential);
      }
      console.error("LoginFormCard missing onGoogleSignIn handler.");
      if (typeof onGoogleError === "function") {
        onGoogleError("Google login is unavailable right now. Please try again.");
      }
      return undefined;
    },
    [onGoogleSignIn, onGoogleError],
  );

  return (
    <div className="relative overflow-hidden rounded-[2.5rem] border border-white/10 bg-white/10 p-10 shadow-[0_35px_120px_-45px_rgba(15,23,42,0.8)] backdrop-blur-2xl transition-all duration-500 md:p-12">
      <div className="grid gap-10 md:grid-cols-[1.1fr,1fr]">
        <LoginHighlights />

        <div className="transition-all duration-500">
          <div className="rounded-2xl bg-white/90 p-6 text-slate-900 shadow-[0_25px_80px_-60px_rgba(15,23,42,0.9)]">
            <div className="space-y-2 text-center">
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-900 text-white shadow-lg shadow-slate-900/30">
                <LogIn size={28} />
              </div>
              <h2 className="text-2xl font-semibold text-slate-900">Sign in</h2>
              <p className="text-sm text-slate-500">Access the Allegro profitability suite</p>
            </div>

            <form className="mt-8 space-y-6" onSubmit={onSubmit}>
              <div className="space-y-4">
                <label className="text-sm font-medium text-slate-600">
                  <span className="sr-only">Email</span>
                  <div className={baseFieldWrapperClasses}>
                    <Mail size={20} className="text-slate-400" />
                    <input
                      type="email"
                      value={emailInput}
                      onChange={(event) => onEmailChange(event.target.value)}
                      placeholder="you@company.com"
                      required
                      className={inputClasses}
                    />
                  </div>
                </label>
                <div className="flex justify-end pt pb-2">
                  <button
                    type="button"
                    onClick={onForgotPassword}
                    className="text-sm font-semibold text-slate-500 hover:text-slate-900 transition"
                  >
                    Forgot password?
                  </button>
                </div>
                <label className="text-sm font-medium text-slate-600">
                  <span className="sr-only">Password</span>
                  <div className={baseFieldWrapperClasses}>
                    <Lock size={20} className="text-slate-400" />
                    <input
                      type={showPassword ? "text" : "password"}
                      value={passwordInput}
                      onChange={(event) => onPasswordChange(event.target.value)}
                      placeholder="••••••••"
                      required
                      className={inputClasses}
                    />
                    <button
                      type="button"
                      className="text-slate-400 transition hover:text-slate-600"
                      onClick={onTogglePassword}
                      aria-label={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </label>
              </div>

              {authError && (
                <div className="rounded-xl border border-rose-200 bg-rose-50/80 px-4 py-3 text-sm text-rose-600">
                  {authError}
                </div>
              )}

              <button type="submit" className={primaryButtonClasses} disabled={loading}>
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="h-2 w-2 animate-pulse rounded-full bg-white" />
                    Signing in…
                  </span>
                ) : (
                  "Sign in"
                )}
              </button>

              <GoogleSignInButton
                loading={loading}
                onCredential={handleGoogleCredential}
                onError={onGoogleError}
                buttonClassName={googleButtonClasses}
                iconWrapperClassName={googleIconWrapperClasses}
              />
            </form>

            <div className="mt-6 text-center text-sm text-slate-600">
              <span>Need an account? </span>
              <button
                type="button"
                onClick={onRegister}
                className="font-semibold text-slate-900 underline decoration-transparent transition hover:decoration-slate-400"
              >
                Create an account
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
